import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import * as eventActions from 'Actions/events';
import { MainAppReducer, PlayInput } from 'Types';
import { InputChangeSource } from 'Types/EventTypes';
import { selectAccountPlayInput } from 'Utils/playAlong/effectiveGrading';
import {
  readStoredAnswer,
  saveDeviceSetup,
  savePlayInput,
  saveTimedOut,
} from 'Utils/playAlong/playInputAccount';
import { reportError } from 'Utils/reportError';

export interface PlayInputChangeOptions {
  /**
   * Called once if a save that ran out of time is found to have landed after
   * all (the caller said it failed; the answer is recorded now).
   */
  onLateSave?: (saved: PlayInput) => void;
}

/** Resolves true once the answer is saved, false when the save failed. */
export type ChangePlayInput = (
  next: PlayInput,
  source: InputChangeSource,
  options?: PlayInputChangeOptions,
) => Promise<boolean>;

/**
 * Change what the account says the player plays on. Every entry point (the
 * device screen, the picker, the MIDI blocker, the keyboard prompt) changes the
 * one account value through this.
 *
 * The app takes the new value only once the backend has saved it, then records
 * the change (its source and the page it was made on). A save that fails
 * changes nothing and records nothing: the caller says so and the player can try
 * again.
 *
 * A save that runs out of time may still land. The account is then read once
 * in the background, and if it holds the answer, the answer is applied and
 * recorded then. That happens at most once for that save, and not at all when
 * another save is under way or the app already holds the answer (for the device
 * screen, the answer and the end of the welcome flow; a press again that
 * succeeded has recorded it), so a change is never recorded twice.
 */
export function usePlayInputChange(): ChangePlayInput {
  const dispatch = useDispatch();
  const accountPlayInput = useSelector(selectAccountPlayInput);
  const accountPlayInputRef = useRef(accountPlayInput);
  accountPlayInputRef.current = accountPlayInput;
  const setupComplete = useSelector(
    (state: MainAppReducer) => state.mainAppReducer?.userData?.welcome_flow_setup_complete,
  );
  const setupCompleteRef = useRef(setupComplete);
  setupCompleteRef.current = setupComplete;
  const savesUnderWay = useRef(0);

  return useCallback<ChangePlayInput>(
    async (next, source, options) => {
      const previous = accountPlayInputRef.current;
      const fromDeviceScreen = source === 'device_screen';
      const surface = window.location.pathname;
      const record = (saved: PlayInput) => {
        dispatch(
          appActions.applyPlayInputAnswer(
            fromDeviceScreen
              ? { play_input: saved, welcome_flow_setup_complete: true }
              : { play_input: saved },
          ),
        );
        dispatch(
          eventActions.inputChangedEventAction({
            playInput: saved,
            previousPlayInput: previous,
            source,
            surface,
          }),
        );
      };
      const recordIfItLanded = async () => {
        try {
          const stored = await readStoredAnswer();
          const landed =
            stored.play_input === next &&
            (!fromDeviceScreen || stored.welcome_flow_setup_complete === true);
          // Already held: the device screen's answer also ends the welcome flow,
          // so there the flag must be held too.
          const alreadyHeld =
            accountPlayInputRef.current === next &&
            (!fromDeviceScreen || setupCompleteRef.current === true);
          if (!landed || savesUnderWay.current > 0 || alreadyHeld) return;
          record(next);
          options?.onLateSave?.(next);
        } catch (error) {
          reportError(error, 'playInput.lateSaveCheck');
        }
      };

      savesUnderWay.current += 1;
      try {
        const saved = fromDeviceScreen ? await saveDeviceSetup(next) : await savePlayInput(next);
        savesUnderWay.current -= 1;
        record(saved);
        return true;
      } catch (error) {
        savesUnderWay.current -= 1;
        reportError(error, 'playInput.save');
        if (saveTimedOut(error)) void recordIfItLanded();
        return false;
      }
    },
    [dispatch],
  );
}
